import { BaseplateSize, BASEPLATE_SIZES, LegoSize } from '../types/LegoTypes';

/** Amplada del patró de lletra en studs (abans d'escalar per mida). */
export const LETTER_PATTERN_STUDS = 5;

/** Separació mínima entre lletres (1 stud). */
export const LETTER_GAP_STUDS = 1;

/** Píxeles per stud a la previsualització (1 stud = 1 cel·la de la graella). */
export const STUD_PX = 10;

export function getStudScale(size: LegoSize): number {
  return size === 'S' ? 1 : size === 'M' ? 2 : 3;
}

/** Amplada física de la lletra a la baseplate (placas PAB 1×1 / 2×2 / 3×3). */
export function getLetterStudWidth(size: LegoSize): number {
  return LETTER_PATTERN_STUDS * getStudScale(size);
}

export function getLetterSlotWidth(size: LegoSize): number {
  return getLetterStudWidth(size) + LETTER_GAP_STUDS;
}

export function getLetterRowHeight(size: LegoSize): number {
  return LETTER_PATTERN_STUDS * getStudScale(size);
}

export function getLetterRowSlotHeight(size: LegoSize): number {
  return getLetterRowHeight(size) + LETTER_GAP_STUDS;
}

export function getLettersPerRow(size: LegoSize, baseplate: BaseplateSize): number {
  const { baseWidth } = BASEPLATE_SIZES[baseplate];
  return Math.floor((baseWidth + LETTER_GAP_STUDS) / getLetterSlotWidth(size));
}

export function getRowsPerBaseplate(size: LegoSize, baseplate: BaseplateSize): number {
  const { baseHeight } = BASEPLATE_SIZES[baseplate];
  return Math.floor((baseHeight + LETTER_GAP_STUDS) / getLetterRowSlotHeight(size));
}

export function getLettersPerBaseplate(size: LegoSize, baseplate: BaseplateSize): number {
  return getLettersPerRow(size, baseplate) * getRowsPerBaseplate(size, baseplate);
}

/** Columna d'inici en studs a la baseplate. */
export function getLetterColumnOffset(letterIndex: number, size: LegoSize): number {
  return letterIndex * getLetterSlotWidth(size);
}

/** Fila d'inici en studs a la baseplate. */
export function getLetterRowOffset(rowIndex: number, size: LegoSize): number {
  return rowIndex * getLetterRowSlotHeight(size);
}

/** Offset vertical en studs per centrar les files de lletres a la baseplate. */
export function getVerticalCenterRowOffset(
  usedLetterRows: number,
  size: LegoSize,
  baseplate: BaseplateSize,
): number {
  if (usedLetterRows === 0) return 0;

  const { baseHeight } = BASEPLATE_SIZES[baseplate];
  const contentHeight =
    usedLetterRows * getLetterRowHeight(size) + (usedLetterRows - 1) * LETTER_GAP_STUDS;
  return Math.floor((baseHeight - contentHeight) / 2);
}

/** Reparte les lletres en files i baseplates segons la mida de la base. */
export function splitNameIntoBaseplates(
  name: string,
  size: LegoSize,
  baseplate: BaseplateSize,
): string[][][] {
  const lettersPerRow = getLettersPerRow(size, baseplate);
  const rowsPerBaseplate = getRowsPerBaseplate(size, baseplate);

  const baseplates: string[][][] = [];
  let currentBaseplate: string[][] = [];
  let currentRow: string[] = [];

  const flushRow = () => {
    if (currentRow.length === 0) return;
    currentBaseplate.push(currentRow);
    currentRow = [];
    if (currentBaseplate.length === rowsPerBaseplate) {
      baseplates.push(currentBaseplate);
      currentBaseplate = [];
    }
  };

  name.split('').forEach((letter) => {
    currentRow.push(letter);
    if (currentRow.length === lettersPerRow) {
      flushRow();
    }
  });

  flushRow();
  if (currentBaseplate.length > 0) {
    baseplates.push(currentBaseplate);
  }

  return baseplates;
}
